// src/pages/buyer/ReorderDashboard.jsx
// Phase 3 — M3: Smart B2B reorder predictions for buyers

import { useEffect, useState } from 'react'
import { aiApi } from '../../api/services'

const COLORS = { primary: '#FF3D7F', secondary: '#FF8500' }

function ConfidenceBadge({ score }) {
  const pct   = Math.round(score * 100)
  const color = score >= 0.8 ? '#10B981' : score >= 0.65 ? '#F59E0B' : '#6B7280'
  const label = score >= 0.8 ? 'High' : score >= 0.65 ? 'Medium' : 'Low'
  return (
    <span style={{ background: `${color}18`, color, padding: '2px 10px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700 }}>
      {label} confidence ({pct}%)
    </span>
  )
}

function DaysUntil({ date }) {
  const d = Math.max(0, Math.ceil((new Date(date) - new Date()) / 86400000))
  const color = d <= 3 ? '#EF4444' : d <= 7 ? '#F59E0B' : '#10B981'
  return (
    <span style={{ color, fontWeight: 700, fontSize: '0.82rem' }}>
      {d === 0 ? 'Today!' : d === 1 ? 'Tomorrow' : `In ${d} days`}
    </span>
  )
}

export default function ReorderDashboard() {
  const [predictions, setPredictions] = useState([])
  const [loading, setLoading]         = useState(true)
  const [daysAhead, setDaysAhead]     = useState(30)
  const [error, setError]             = useState('')

  useEffect(() => {
    setLoading(true)
    aiApi.reorderPredictions(daysAhead)
      .then(r => setPredictions(r.data?.data?.predictions ?? r.data?.predictions ?? []))
      .catch(() => setError('Could not load predictions.'))
      .finally(() => setLoading(false))
  }, [daysAhead])

  const urgent   = predictions.filter(p => {
    const d = Math.ceil((new Date(p.predicted_reorder_date) - new Date()) / 86400000)
    return d <= 7
  })
  const upcoming = predictions.filter(p => {
    const d = Math.ceil((new Date(p.predicted_reorder_date) - new Date()) / 86400000)
    return d > 7
  })

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 800, fontSize: '1.8rem', color: '#1A1A2E', margin: '0 0 4px' }}>
          Smart Reorder Predictions
        </h1>
        <p style={{ color: '#9CA3AF', margin: 0, fontSize: '0.88rem' }}>
          AI-powered predictions based on your order history — never run out of stock.
        </p>
      </div>

      {/* Filter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {[14, 30, 60, 90].map(d => (
          <button key={d} onClick={() => setDaysAhead(d)} style={{
            padding: '7px 16px', borderRadius: 20, fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer',
            border: `1.5px solid ${daysAhead === d ? COLORS.primary : '#E5E7EB'}`,
            background: daysAhead === d ? '#FFF0F5' : '#fff',
            color: daysAhead === d ? COLORS.primary : '#6B7280',
          }}>Next {d} days</button>
        ))}
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '60px', color: '#9CA3AF' }}>Analysing your order history…</div>
      )}

      {error && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 12, padding: '14px 18px', color: '#EF4444', fontSize: '0.85rem' }}>
          {error}
        </div>
      )}

      {!loading && !error && predictions.length === 0 && (
        <div style={{ background: '#fff', borderRadius: 16, border: '1.5px solid #F0F0F0', padding: '60px', textAlign: 'center' }}>
          <p style={{ fontSize: '2.5rem', marginBottom: 8 }}>📊</p>
          <p style={{ fontWeight: 700, color: '#1A1A2E', marginBottom: 6 }}>No predictions yet</p>
          <p style={{ color: '#9CA3AF', fontSize: '0.85rem' }}>
            We need at least 2 orders of the same product to generate predictions. Keep ordering and we'll start predicting your reorder needs.
          </p>
        </div>
      )}

      {!loading && urgent.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <h2 style={{ fontWeight: 700, fontSize: '0.95rem', color: '#EF4444', margin: '0 0 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
            🚨 Reorder Now ({urgent.length})
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
            {urgent.map(p => <PredictionCard key={p.id} prediction={p} urgent />)}
          </div>
        </div>
      )}

      {!loading && upcoming.length > 0 && (
        <div>
          <h2 style={{ fontWeight: 700, fontSize: '0.95rem', color: '#374151', margin: '0 0 14px' }}>
            📅 Coming Up ({upcoming.length})
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
            {upcoming.map(p => <PredictionCard key={p.id} prediction={p} />)}
          </div>
        </div>
      )}

      {/* How it works */}
      <div style={{ marginTop: 36, background: '#F9FAFB', borderRadius: 16, padding: '20px 24px' }}>
        <h3 style={{ fontWeight: 700, fontSize: '0.88rem', color: '#374151', margin: '0 0 10px' }}>💡 How predictions work</h3>
        <p style={{ color: '#6B7280', fontSize: '0.82rem', lineHeight: 1.7, margin: 0 }}>
          We analyse your historical order intervals for each product. When you consistently order every 30 days, we predict your next order date and alert you 7 days early. Confidence increases with more order history.
        </p>
      </div>
    </div>
  )
}

function PredictionCard({ prediction: p, urgent }) {
  const product  = p.product || {}
  const supplier = p.supplier || {}
  const imgUrl   = product.primaryImage?.url || product.primary_image?.url

  return (
    <div style={{
      background: '#fff', borderRadius: 16,
      border: `1.5px solid ${urgent ? '#FECACA' : '#F0F0F0'}`,
      padding: 18, display: 'flex', flexDirection: 'column', gap: 12,
    }}>
      {/* Product info */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        {imgUrl
          ? <img src={imgUrl} alt={product.name} style={{ width: 52, height: 52, borderRadius: 10, objectFit: 'cover', flexShrink: 0 }} />
          : <div style={{ width: 52, height: 52, borderRadius: 10, background: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', flexShrink: 0 }}>🧵</div>
        }
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontWeight: 700, color: '#1A1A2E', margin: '0 0 2px', fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {product.name || 'Product'}
          </p>
          <p style={{ color: '#9CA3AF', margin: 0, fontSize: '0.75rem' }}>
            {supplier.business_name || supplier.name || 'Supplier'}
          </p>
        </div>
      </div>

      {/* Prediction details */}
      <div style={{ background: urgent ? '#FEF2F2' : '#F9FAFB', borderRadius: 10, padding: '10px 12px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ color: '#6B7280', fontSize: '0.78rem' }}>Predicted reorder</span>
          <DaysUntil date={p.predicted_reorder_date} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ color: '#6B7280', fontSize: '0.78rem' }}>Suggested qty</span>
          <span style={{ fontWeight: 700, color: '#374151', fontSize: '0.82rem' }}>{p.predicted_quantity} units</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ color: '#6B7280', fontSize: '0.78rem' }}>Order frequency</span>
          <span style={{ fontWeight: 600, color: '#374151', fontSize: '0.78rem' }}>Every ~{p.order_frequency_days} days</span>
        </div>
      </div>

      <ConfidenceBadge score={p.confidence_score} />

      <a
        href={`/catalogue/product/${product.slug || product.id}`}
        style={{
          display: 'block', textAlign: 'center', padding: '9px',
          background: urgent
            ? `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.secondary})`
            : '#F3F4F6',
          color: urgent ? '#fff' : '#374151',
          borderRadius: 10, fontWeight: 700, fontSize: '0.83rem', textDecoration: 'none',
        }}
      >
        {urgent ? '🛒 Reorder Now' : '🛒 View Product'}
      </a>
    </div>
  )
}
